import { useEffect, useRef, useState, Dispatch, SetStateAction } from "react";
import classNames from "classnames/bind";
import { IconButton } from "@JohnLewisPartnership/jl-design-system/dist/components/button/Button";
import { CloseOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/CloseOutlined";
import { ZoomModalImage } from "./ZoomModalImage";
import { ZoomModalVideo } from "./ZoomModalVideo";
import { Thumbnails } from "./Thumbnails";
import { GalleryObject } from "@/components/Galleries/utils/utils.ts";
import { dispatchGalleryMonetateQEvent } from "@/components/Galleries/components/Analytics/dispatchGalleryMonetateQEvent";
import { useStandardProps } from "@/context/StandardPropsContext/StandardPropsContext";
import styles from "./Carousel.module.scss";

const cx = classNames.bind(styles);

type CarouselProps = {
  mediaItems: GalleryObject[];
  selectedIndex: number;
  setSelectedIndex: Dispatch<SetStateAction<number>>;
  closeZoomModal: () => void;
};

type CarouselItemProps = {
  mediaItem: GalleryObject;
  totalMediaItems: number;
  index: number;
  intersectionObserver: IntersectionObserver | null;
  scrollToIndex: (index: number) => void;
  isActiveVideo: boolean;
  onVideoPlayStateChange: (index: number, isPlaying: boolean) => void;
};

const CarouselItem = ({
  mediaItem,
  totalMediaItems,
  index,
  intersectionObserver,
  scrollToIndex,
  isActiveVideo,
  onVideoPlayStateChange,
}: CarouselItemProps) => {
  const itemRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (itemRef.current && intersectionObserver) {
      intersectionObserver.observe(itemRef.current);
    }

    return () => {
      if (itemRef.current && intersectionObserver) {
        intersectionObserver.unobserve(itemRef.current);
      }
    };
  }, [intersectionObserver]);

  if (!mediaItem) {
    return null;
  }

  return (
    <div
      key={mediaItem.imageUrl}
      data-gallery-item-index={index}
      className={styles.galleryItem}
      ref={itemRef}
    >
      {mediaItem.type === "video" ? (
        <ZoomModalVideo
          video={mediaItem}
          index={index}
          total={totalMediaItems}
          scrollToIndex={scrollToIndex}
          isActive={isActiveVideo}
          onPlayStateChange={onVideoPlayStateChange}
        />
      ) : (
        <ZoomModalImage
          mediaItem={mediaItem}
          index={index}
          total={totalMediaItems}
          onClick={() => {
            dispatchGalleryMonetateQEvent("ClickOnModalImage", "imageModal");
          }}
        />
      )}
    </div>
  );
};

export const Carousel = ({
  mediaItems,
  selectedIndex,
  setSelectedIndex,
  closeZoomModal,
}: CarouselProps) => {
  const mainContainerRef = useRef<HTMLDivElement>(null);
  const thumbsContainerRef = useRef<HTMLDivElement>(null);
  const [fullscreenAnimationEnded, setfullscreenAnimationEnded] =
    useState(false);
  const [intersectionObserver, setIntersectionObserver] =
    useState<IntersectionObserver | null>(null);
  const [autoScroll, setAutoScroll] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [activeVideoIndex, setActiveVideoIndex] = useState<number | null>(null);
  const { flags } = useStandardProps();
  const hasDeferredMonetateQEvents = !!flags.hasDeferredMonetateQEvents.value;
  const modalMode =
    mediaItems[0]?.type === "video" ? "videoModal" : "imageModal";

  const handleVideoPlayStateChange = (index: number, playing: boolean) => {
    setActiveVideoIndex((current) => {
      if (playing) return index;
      return current === index ? null : current;
    });
  };

  const scrollToIndex = async (index: number) => {
    if (index < 0 || index >= mediaItems.length || !mainContainerRef.current) {
      return;
    }
    const target = mainContainerRef.current.children[index];
    if (!(target instanceof HTMLElement)) return;

    let thumbnailHeight = 0;
    if (
      thumbsContainerRef.current &&
      !window.matchMedia("(min-width: 1024px)").matches
    ) {
      thumbnailHeight =
        thumbsContainerRef.current.getBoundingClientRect().height;
    }
    const difference =
      (window.innerHeight - thumbnailHeight - target.clientHeight) / 2;
    const offsetTop =
      difference < 0
        ? target.offsetTop + Math.abs(difference)
        : target.offsetTop - Math.abs(difference);

    setAutoScroll(true);
    const result = await mainContainerRef.current.scrollTo(
      target.offsetLeft,
      offsetTop,
    );
    setAutoScroll(false);
  };

  const handleFullscreenAnimationEnd = () => {
    setfullscreenAnimationEnded(true);
    scrollToIndex(selectedIndex);
  };

  const handleIntersection = (entries: IntersectionObserverEntry[]) => {
    const intersectingEntry = entries.find(
      ({ isIntersecting }) => isIntersecting,
    );

    if (mainContainerRef.current && intersectingEntry) {
      const intersectionIndex = [...mainContainerRef.current.children].indexOf(
        intersectingEntry.target,
      );

      dispatchGalleryMonetateQEvent(
        "CarouselInteraction",
        modalMode,
        hasDeferredMonetateQEvents,
      );

      if (!autoScroll) {
        setSelectedIndex(intersectionIndex);
      }
    }
  };

  const handleScrollIntoView = () => {
    if (mainContainerRef.current) {
      mainContainerRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    let observer: IntersectionObserver;
    if (fullscreenAnimationEnded && !intersectionObserver) {
      observer = new IntersectionObserver(handleIntersection, {
        root: mainContainerRef.current,
        threshold: 0.3,
      });

      setIntersectionObserver(observer);
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, [handleIntersection, intersectionObserver, fullscreenAnimationEnded]);

  useEffect(() => {
    const modalElement = mainContainerRef.current?.closest(
      '[data-testid="modal"]',
    );

    modalElement?.addEventListener(
      "animationstart",
      handleFullscreenAnimationEnd,
      {
        once: true,
      },
    );

    document.addEventListener(
      "carousel-gallery-scroll-into-view",
      handleScrollIntoView,
    );

    const root = document?.documentElement;
    if (root) {
      root.classList.forEach((value) => {
        if (value.includes("scrollbar-shift")) {
          root.classList.remove(value);
        }
      });
    }

    return () => {
      modalElement?.removeEventListener(
        "animationstart",
        handleFullscreenAnimationEnd,
      );

      document.removeEventListener(
        "carousel-gallery-scroll-into-view",
        handleScrollIntoView,
      );
    };
  }, []);

  useEffect(() => {
    const isDesktop = window.matchMedia("(min-width: 1024px)");

    const handleResize = () => {
      setIsDesktop(isDesktop.matches);
    };
    handleResize();

    isDesktop.addEventListener("change", handleResize);

    return () => {
      window.removeEventListener("change", handleResize);
    };
  }, []);

  return (
    <div className={styles.wrapper} data-testid={"gallery:zoom:modal:carousel"}>
      <div className={styles.main}>
        <div
          className={cx(styles.gallery, {
            loading: !fullscreenAnimationEnded,
          })}
          ref={mainContainerRef}
        >
          {mediaItems.map((mediaItem, ind) => (
            <CarouselItem
              mediaItem={mediaItem}
              totalMediaItems={mediaItems.length}
              intersectionObserver={intersectionObserver}
              index={ind}
              scrollToIndex={scrollToIndex}
              isActiveVideo={activeVideoIndex === ind}
              onVideoPlayStateChange={handleVideoPlayStateChange}
              key={`${mediaItem.imageUrl}-${ind}`}
            />
          ))}
        </div>
        <Thumbnails
          media={mediaItems}
          selectedIndex={selectedIndex}
          scrollImagesToIndex={scrollToIndex}
          autoScroll={autoScroll}
          ref={thumbsContainerRef}
        />
      </div>
      <IconButton
        icon={CloseOutlined}
        aria-label={"Close button"}
        data-testid="gallery:zoom:modal:close:button"
        className={styles.closeButton}
        onClick={closeZoomModal}
        background="filled"
        size={isDesktop ? "medium" : "small"}
      />
      <div
        className={styles.visuallyHidden}
        aria-live="polite"
        aria-atomic="true"
      >
        {`Viewing image ${selectedIndex + 1} of ${mediaItems.length}`}
      </div>
    </div>
  );
};
